import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAccount, useBalance, useReadContract } from "wagmi";
import { disconnect } from "wagmi/actions";
import { formatUnits, zeroAddress } from "viem";
import { notifySuccess } from "../../../hooks/utils";
import styled from "styled-components";
import BigcoinTokenContractABI from "../../../abis/bigcointoken.abi.json";
import "./accountmodal.css"
import { BIGCOINTOKEN_ADDRESS, TOKEN_SYMBOL } from "../../../utils/constants";
import { config } from "../../../config";
import { useTranslation } from "react-i18next";
import { setCookie } from "../../../utils";

interface AccountModalProps {
  open: boolean;
  onClose: () => void;
}

const AccountModal: React.FC<AccountModalProps> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const account = useAccount();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
    setCookie('i18next', lng, 7); // Save for 7 days 
  };


  const userBalance = useBalance({
    address: account ? account.address : zeroAddress,
  })

  const { data: bigTokenBalance } = useReadContract({
    address: BIGCOINTOKEN_ADDRESS,
    abi: BigcoinTokenContractABI,
    functionName: 'balanceOf',
    args: [account ? account.address : zeroAddress as any]
  });

  const bigTokenBalanceFormatted = useMemo(() => {
    if (bigTokenBalance) {
      return Number(formatUnits(bigTokenBalance as any, 18)).toLocaleString("en-US", { maximumFractionDigits: 10 });
    }
    return "0.0000000000";
  }, [bigTokenBalance]);

  const handleDisconnect = async () => {
    await disconnect(config); // Disconnects the wallet
    onClose();          // Closes the modal
  };


  // MODAL OPEN AND CLOSE
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose]);


  // onclick function to copy the wallet address to clipboard
  const copyToClipboard = () => {
    if (account) {
      navigator.clipboard.writeText(account ? account.address as any : "").then(() => {
        notifySuccess("Wallet address copied to clipboard!");
      }).catch((err) => {
        console.error("Failed to copy: ", err);
      });
    }
  };

  if (!open) return null;

  return (
    <div data-state="open" className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ pointerEvents: "auto" }} data-aria-hidden="true" aria-hidden="true">
      <div
        ref={modalRef}
        role="dialog"
        id="radix-:r4f:"
        aria-describedby="radix-:r4h:"
        aria-labelledby="radix-:r4g:"
        data-state="open"
        className="text-sm fixed left-[50%] top-[50%] z-50 grid w-full max-w-modal translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-slate-800 dark:bg-slate-950 bg-primary text-white"
        tabIndex={-1}
        style={{ pointerEvents: "auto", border: "none", background: "rgb(16, 44, 57)" }}
      >


        <div className="sc-aa05954f-3 gnIWBN">
          <div className="sc-f9aa7acf-0 UcrLL">
            <h2 className="sc-f9aa7acf-1 hpjeZI">{t("account_modal.title")}</h2>
            <div className="sc-f9aa7acf-2 iOXaiB">
              <p className="sc-f9aa7acf-3 dMUHUX">
                {t("account_modal.subtitle")}
              </p>
            </div>
            <div className="sc-f9aa7acf-4 jHiCTA">
              <div className="sc-f9aa7acf-5 hDOKPk">
                <span className="sc-f9aa7acf-6 hyMdLF">{t("account_modal.wallet_address")}</span>
                <div className="sc-f9aa7acf-7 imFDOb">
                  <span className="sc-f9aa7acf-8 ffbJSK">
                    {account ? account.address?.substring(0, 12) + "..." + account.address?.substring(30, 42) : ""}
                  </span>
                  <button className="sc-cdc39d4e-0 gvrgXY" onClick={copyToClipboard}>{t("account_modal.copy")}</button>
                </div>
              </div>
              <div className="sc-f9aa7acf-9 kEiXqJ">
                <div className="sc-f9aa7acf-10 hpWmMv">
                  <span className="sc-f9aa7acf-11 jLfXIn">S {t("account_modal.balance")}</span>
                  <span className="sc-f9aa7acf-12 jSUFTy">{userBalance ? Number(formatUnits(userBalance.data ? userBalance.data.value.toString() : "0.00000" as any, 18)).toFixed(5) : "0.00000"} S</span>
                </div>
                <div className="sc-f9aa7acf-10 hpWmMv">
                  <span className="sc-f9aa7acf-11 jLfXIn">{TOKEN_SYMBOL} {t("account_modal.balance")}</span>
                  <span className="sc-f9aa7acf-12 jSUFTy">{bigTokenBalanceFormatted} {TOKEN_SYMBOL}</span>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginBottom: "1rem" }}>
                <button className="sc-cdc39d4e-0 gvrgXY" style={{ width: "100%" }} onClick={() => changeLanguage('en')}>
                  English
                </button>
                <button className="sc-cdc39d4e-0 gvrgXY" style={{ width: "100%" }} onClick={() => changeLanguage('cn')}>
                  中文
                </button>
              </div>
              <div className="sc-f9aa7acf-13 hldCrC">
                <span className="sc-f9aa7acf-14 dZuXQO">
                  {t("account_modal.bridge_description")}
                </span>
                <a
                  href="https://my.soniclabs.com/bridge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sc-f9aa7acf-15 isDrwf"
                >
                  {t("account_modal.bridge_btn")} →
                </a>
              </div>
            </div>
            <div className="sc-f9aa7acf-16 jIiFXB">
              <button className="sc-cdc39d4e-0 gvrgXY" onClick={onClose}>{t("account_modal.close")}</button>
              <button className="sc-cdc39d4e-0 sc-f9aa7acf-17 gvrgXY koiKsu" onClick={handleDisconnect}>
                {t("account_modal.disconnect")}
              </button>
            </div>
          </div>
        </div>




      </div>
    </div>
  );
}
export default AccountModal;